import { Badge } from "@/components/ui/badge";

/**
 * The fulfilment stage of a single order, as a pill.
 *
 * Sits immediately after <PaymentStatusBadge>, and the two split the work cleanly:
 * PaymentStatusBadge owns every word about *payment*, this owns every word about
 * *fulfilment*. Before that split both pills rendered "Payment failed" for a declined
 * order, so the same status appeared twice in one cell.
 *
 * Extracted so the orders list and the case popup state the stage identically — the same
 * reason shared/PaymentStatusBadge.jsx and shared/Pagination.jsx were extracted.
 */
const FULFILMENT_TONE = "border-indigo-200 bg-indigo-50 text-indigo-700";
const HALTED_TONE = "border-red-200 bg-red-50 text-red-700";

export const FULFILMENT_STAGES = [
  { key: "placed", label: "Order Placed", match: ["pending", "confirmed"] },
  { key: "submitted", label: "Order Submitted", match: ["processing"] },
  { key: "shipped", label: "Order Shipped", match: ["shipped", "delivered"] },
];

// Never fall back to FULFILMENT_STAGES[0]: an unmatched status used to render as "Order
// Placed", so a declined order looked like a received one (TR-25 item 3, and the case
// popup did the same via `stageIndex` returning 0). Show the raw status instead, which is
// at worst ugly and at best tells the operator something true.
export const stageLabelOf = (s) =>
  FULFILMENT_STAGES.find((x) => x.match.includes(s))?.label || s || "—";

/**
 * @param {object} order  needs `status` and `payment_status`
 * @returns the stage pill, or null when there is no fulfilment to report.
 */
export default function OrderStageBadge({ order, className = "" }) {
  const status = order?.status;
  // A declined order never entered fulfilment, and PaymentStatusBadge is already saying
  // "Payment failed" right next to us. Gating on both fields is deliberate: it drops the
  // pill only in the exact duplicated case, so the cell can never come out empty.
  if (status === "failed" && order?.payment_status === "failed") return null;

  // `cancelled` shares the filter bucket with `failed` but not its wording — a cancelled
  // order may well have been paid, and calling that "Payment Failed" is simply untrue.
  const label =
    status === "failed" ? "Payment Failed"
    : status === "cancelled" ? "Order Cancelled"
    : stageLabelOf(status);
  const tone = status === "failed" || status === "cancelled" ? HALTED_TONE : FULFILMENT_TONE;

  return <Badge variant="outline" className={`${tone} ${className}`}>{label}</Badge>;
}
